import { memo, useCallback, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';

import { Modal, Button, Radio, Input, DatePicker, Checkbox, Typography } from 'antd';

import {
  CandidateOfferEventType,
  CandidateOfferType,
} from '../../../../../fsm/jobs';

import styles from './index.module.css';

const { TextArea } = Input;
const { Text } = Typography;

export interface RecordOfferEventPayload {
  offerType: CandidateOfferType;
  compensationSummary?: string;
  proposedStartDate?: string;
  notes?: string;
}

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  /** Fixed by which button opened the modal - never chosen inside it. */
  eventType: CandidateOfferEventType;
  candidateName?: string;
  /** Who is about to confirm this - resolved from their own account, never editable here. */
  confirmingUserName: string;
  confirmingUserRole: string;
  onClose: () => void;
  onSubmit: (payload: RecordOfferEventPayload) => void;
}

const copyFor = (eventType: CandidateOfferEventType, candidateName?: string) => {
  const who = candidateName ? ` for ${candidateName}` : '';
  if (eventType === CandidateOfferEventType.OFFER_ACCEPTED) {
    return {
      title: 'Mark offer accepted',
      confirmLabel: 'Record offer accepted',
      confirmationCopy: `I confirm the candidate${who} accepted this offer. This creates a permanent record used for billing.`,
    };
  }
  return {
    title: 'Mark offer made',
    confirmLabel: 'Record offer made',
    confirmationCopy: `I confirm this offer${who} was made. This creates a permanent record used for billing.`,
  };
};

/**
 * Records a billing-triggering offer event. Two things this modal exists to
 * enforce, both from a persona-testing finding on the original prototype
 * (a single click with an optional note created a permanent record with no
 * corroboration):
 *
 * 1. A deliberate, typed-through confirmation - not a passive modal with a
 *    default-focused "Confirm" button. The submit action is disabled until the
 *    reviewer actively checks a statement naming the consequence, not until
 *    they've merely opened the modal.
 * 2. Structured terms (how the offer was communicated, compensation, start
 *    date) rather than one free-text note, so the record says something
 *    without anyone having to read prose.
 *
 * Who is recording it is shown, not asked - `confirmingUserName`/`Role` come
 * from the caller's own resolved account, the same identity the backend
 * independently derives server-side from the auth token. This component
 * cannot invent or edit that identity.
 */
const RecordOfferEventModal = ({
  isOpen,
  isLoading,
  eventType,
  candidateName,
  confirmingUserName,
  confirmingUserRole,
  onClose,
  onSubmit,
}: Props) => {
  const [offerType, setOfferType] = useState<CandidateOfferType | undefined>();
  const [compensationSummary, setCompensationSummary] = useState('');
  const [proposedStartDate, setProposedStartDate] = useState<Dayjs | null>(null);
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const reset = useCallback(() => {
    setOfferType(undefined);
    setCompensationSummary('');
    setProposedStartDate(null);
    setNotes('');
    setConfirmed(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleSubmit = useCallback(() => {
    if (!offerType || !confirmed) {
      return;
    }
    onSubmit({
      offerType,
      compensationSummary: compensationSummary.trim() || undefined,
      proposedStartDate: proposedStartDate
        ? proposedStartDate.format('YYYY-MM-DD')
        : undefined,
      notes: notes.trim() || undefined,
    });
    reset();
    onClose();
  }, [
    offerType,
    confirmed,
    compensationSummary,
    proposedStartDate,
    notes,
    onSubmit,
    reset,
    onClose,
  ]);

  const { title, confirmLabel, confirmationCopy } = copyFor(
    eventType,
    candidateName,
  );

  return (
    <Modal
      title={title}
      open={isOpen}
      confirmLoading={isLoading}
      onCancel={handleClose}
      footer={
        <>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={!offerType || !confirmed}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className={styles.content}>
        <Text type="warning" className={styles.billingNotice}>
          This creates a permanent record and triggers Avrio&apos;s billing for
          this placement. It cannot be edited or deleted once recorded.
        </Text>

        <div className={styles.field}>
          <Text strong>How was the offer communicated?</Text>
          <Radio.Group
            value={offerType}
            onChange={(e) => setOfferType(e.target.value)}
          >
            <Radio value={CandidateOfferType.VERBAL}>Verbal</Radio>
            <Radio value={CandidateOfferType.WRITTEN}>Written</Radio>
          </Radio.Group>
        </div>

        <div className={styles.field}>
          <Text strong>Compensation summary</Text>
          <Text type="secondary" className={styles.hint}>
            Optional. Free text - e.g. &quot;$150k base + 15% bonus&quot;.
          </Text>
          <Input
            value={compensationSummary}
            onChange={(e) => setCompensationSummary(e.target.value)}
            maxLength={255}
            placeholder="e.g. $150k base + 15% bonus"
          />
        </div>

        <div className={styles.field}>
          <Text strong>Proposed start date</Text>
          <DatePicker
            value={proposedStartDate}
            onChange={setProposedStartDate}
            style={{ width: '100%' }}
            disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
          />
        </div>

        <div className={styles.field}>
          <Text strong>Notes</Text>
          <TextArea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            showCount
            rows={3}
            maxLength={500}
            placeholder="Optional"
          />
        </div>

        <div className={styles.confirmationBlock}>
          <Text type="secondary" className={styles.hint}>
            Recording as {confirmingUserName} ({confirmingUserRole})
          </Text>
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          >
            {confirmationCopy}
          </Checkbox>
        </div>
      </div>
    </Modal>
  );
};

export default memo(RecordOfferEventModal);
